import { useState, useCallback, useId } from 'react';
import { Check, FlaskConical, AlertTriangle, Zap } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { copperCycleColors } from '@/config/experiments/chem1131/experiment5.config';

const stepInfoContent: Record<number, { title: string; content: string; whatHappens: string; observe: string[] }> = {
  1: {
    title: 'Adding Zinc Powder',
    content: 'Zinc is more reactive than copper in the activity series of metals. This means zinc can displace copper from its compounds. We use 2.0 g of zinc powder (excess) to ensure complete reduction of Cu²⁺ to Cu metal.',
    whatHappens: 'Zinc powder is added in small portions while stirring. The fine powder provides high surface area for the reaction. An immediate reaction begins at the zinc surface.',
    observe: [
      'Gray zinc powder disperses in solution',
      'Bubbles begin forming (H₂ gas)',
      'Solution color starts to fade',
      'Copper-colored precipitate may appear'
    ]
  },
  2: {
    title: 'Observing the Displacement Reaction',
    content: 'This is a single displacement (redox) reaction:\n\nCuSO₄(aq) + Zn(s) -> ZnSO₄(aq) + Cu(s)\n\nZinc is oxidized: Zn -> Zn²⁺ + 2e-\nCopper is reduced: Cu²⁺ + 2e- -> Cu\n\nSide reaction produces H₂: Zn + H₂SO₄ -> ZnSO₄ + H₂',
    whatHappens: 'The blue solution gradually fades as Cu²⁺ ions are reduced to copper metal. Zinc dissolves as it is oxidized to Zn²⁺. Hydrogen gas bubbles off from the acidic solution.',
    observe: [
      'Blue color fading toward colorless',
      'Vigorous H₂ gas evolution',
      'Copper-colored precipitate accumulating',
      'Zinc particles disappearing'
    ]
  },
  3: {
    title: 'Heating to Complete Reaction',
    content: 'Gentle heating increases the reaction rate by providing more kinetic energy to the reactants. This ensures complete conversion of all Cu²⁺ ions to copper metal. The solution should become completely colorless.',
    whatHappens: 'Heat accelerates the displacement reaction. Any remaining blue color disappears as the last Cu²⁺ ions are reduced. The copper precipitate becomes more visible.',
    observe: [
      'Solution becomes completely colorless (ZnSO₄)',
      'All blue color has disappeared',
      'Copper precipitate clearly visible at bottom',
      'Reaction essentially complete'
    ]
  }
};

// H₂ Bubbles animation
function H2Bubbles({ active }: { active: boolean }) {
  if (!active) return null;
  return (
    <g>
      {[...Array(10)].map((_, i) => (
        <circle
          key={i}
          cx={40 + (i % 5) * 20}
          cy={120}
          r={2 + (i % 3)}
          fill="rgba(255,255,255,0.5)"
        >
          <animate
            attributeName="cy"
            values="120;40;120"
            dur={`${0.8 + (i % 4) * 0.2}s`}
            repeatCount="indefinite"
            begin={`${i * 0.15}s`}
          />
          <animate
            attributeName="opacity"
            values="0.5;0.1;0.5"
            dur={`${0.8 + (i % 4) * 0.2}s`}
            repeatCount="indefinite"
            begin={`${i * 0.15}s`}
          />
        </circle>
      ))}
    </g>
  );
}

export default function Part6Reduction() {
  const [zincAdded, setZincAdded] = useState(false);
  const [reacting, setReacting] = useState(false);
  const [reactionProgress, setReactionProgress] = useState(0);
  const [heatingStarted, setHeatingStarted] = useState(false);
  const [reactionComplete, setReactionComplete] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const id = useId();

  const completedSteps = [zincAdded, reactionComplete].filter(Boolean).length;

  const handleAddZinc = useCallback(() => {
    if (!zincAdded) {
      setZincAdded(true);
      setReacting(true);
      setCurrentStep(2);

      // Animate color fading
      let progress = 0;
      const interval = setInterval(() => {
        progress += 0.02;
        setReactionProgress(Math.min(progress, 0.7));
        if (progress >= 0.7) {
          clearInterval(interval);
          setCurrentStep(3);
        }
      }, 100);
    }
  }, [zincAdded]);

  const handleHeat = useCallback(() => {
    if (zincAdded && !heatingStarted && reactionProgress >= 0.7) {
      setHeatingStarted(true);

      // Complete the reaction
      let progress = reactionProgress;
      const interval = setInterval(() => {
        progress += 0.02;
        setReactionProgress(progress);
        if (progress >= 1) {
          clearInterval(interval);
          setReacting(false);
          setReactionComplete(true);
        }
      }, 100);
    }
  }, [zincAdded, heatingStarted, reactionProgress]);

  // Color transition: blue CuSO₄ -> colorless ZnSO₄
  const getSolutionColor = () => {
    if (reactionComplete) return copperCycleColors.znSO4Supernatant;
    if (reactionProgress > 0.8) return copperCycleColors.cuSO4ZnPhase4;
    if (reactionProgress > 0.6) return copperCycleColors.cuSO4ZnPhase3;
    if (reactionProgress > 0.4) return copperCycleColors.cuSO4ZnPhase2;
    if (reactionProgress > 0.2) return copperCycleColors.cuSO4ZnPhase1;
    return copperCycleColors.cuSO4Solution;
  };

  const getCopperAmount = () => {
    return Math.min(1, reactionProgress * 1.2);
  };

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part VIII: CuSO₄ + Zn (Redox)</h3>
        </div>
        <div className="safety-warning">
          <AlertTriangle size={14} />
          <span>FUME HOOD - H₂ gas is flammable!</span>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Add Zinc Powder', hint: 'Add 2.0 g zinc in portions', done: zincAdded },
            { id: 2, title: 'Stir & Observe', hint: 'Watch color fade to colorless', done: reactionProgress >= 0.7 },
            { id: 3, title: 'Heat Gently', hint: 'Speed up completion', done: reactionComplete }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Color fading progress */}
        <div className="color-progress">
          <span className="label">Color Fading Progress:</span>
          <div className="fade-bar">
            <div
              className="fade-fill"
              style={{
                width: `${reactionProgress * 100}%`,
                background: `linear-gradient(90deg, ${copperCycleColors.cuSO4Solution}, ${copperCycleColors.cuSO4ZnPhase2}, ${copperCycleColors.cuSO4ZnPhase4}, #FFFFFF)`
              }}
            />
          </div>
          <div className="color-labels">
            <span style={{ color: copperCycleColors.cuSO4Solution }}>Blue</span>
            <span style={{ color: '#888' }}>Colorless</span>
          </div>
        </div>
      </div>

      <LabBench
        experimentName="Copper Displacement by Zinc"
        completedCount={completedSteps}
        totalSteps={2}
        equipment={['CuSO₄ solution', 'Zinc powder', 'Hot plate']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="reaction-workspace">
          <div className="equipment-row">
            {/* Zinc Powder Container */}
            <div
              className={`equipment-station ${currentStep === 1 && !zincAdded ? 'clickable' : ''}`}
              onClick={handleAddZinc}
              style={{ cursor: currentStep === 1 && !zincAdded ? 'pointer' : 'default' }}
            >
              <svg viewBox="0 0 50 70" width={50} height={70}>
                <rect x="10" y="15" width="30" height="40" fill="#555" stroke="#777" strokeWidth="1" rx="3" />
                {/* Zinc powder visual */}
                <ellipse cx="25" cy="35" rx="12" ry="8" fill={copperCycleColors.zincPowder} />
                <g>
                  {[...Array(5)].map((_, i) => (
                    <circle key={i} cx={18 + i * 3} cy={30 + (i % 2) * 5} r={1.5} fill="#B8B8B8" />
                  ))}
                </g>
                <text x="25" y="65" fontSize="7" fill="#E0E0E0" textAnchor="middle">Zn powder</text>
              </svg>
              <span className="equipment-label">2.0 g Zinc</span>
            </div>

            {/* Reaction Beaker */}
            <div
              className={`equipment-station ${currentStep === 3 && !reactionComplete ? 'clickable' : ''}`}
              onClick={handleHeat}
              style={{ cursor: currentStep === 3 && !reactionComplete ? 'pointer' : 'default' }}
            >
              <svg viewBox="0 0 180 240" width={200} height={260}>
                <defs>
                  <linearGradient id={`beaker-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="rgba(170,200,220,0.7)" />
                    <stop offset="50%" stopColor="rgba(200,230,245,0.15)" />
                    <stop offset="100%" stopColor="rgba(170,200,220,0.6)" />
                  </linearGradient>
                  <linearGradient id={`solution-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor={getSolutionColor()} stopOpacity="0.85" />
                    <stop offset="100%" stopColor={getSolutionColor()} stopOpacity="0.95" />
                  </linearGradient>
                </defs>

                {/* Beaker */}
                <rect x="35" y="30" width="110" height="140" fill={`url(#beaker-${id})`} stroke="#7A9BB8" strokeWidth="2" rx="2" />

                {/* Solution with animated color change */}
                <rect x="38" y="55" width="104" height="112" fill={`url(#solution-${id})`}>
                  {reacting && (
                    <animate
                      attributeName="fill"
                      values={`${copperCycleColors.cuSO4Solution};${copperCycleColors.cuSO4ZnPhase2};${copperCycleColors.cuSO4ZnPhase4};#FFFFFF`}
                      dur="10s"
                      fill="freeze"
                    />
                  )}
                </rect>

                {/* H₂ bubbles */}
                <H2Bubbles active={reacting} />

                {/* Zinc powder dissolving */}
                {zincAdded && !reactionComplete && (
                  <g opacity={1 - reactionProgress * 0.8}>
                    {[...Array(6)].map((_, i) => (
                      <circle
                        key={i}
                        cx={50 + (i % 3) * 30}
                        cy={130 + Math.floor(i / 3) * 15}
                        r={4 - i * 0.3}
                        fill={copperCycleColors.zincPowder}
                      >
                        {reacting && (
                          <animate attributeName="r" values={`${4 - i * 0.3};0`} dur="8s" fill="freeze" />
                        )}
                      </circle>
                    ))}
                  </g>
                )}

                {/* Copper precipitate forming */}
                <g opacity={getCopperAmount()}>
                  <ellipse cx="90" cy="160" rx={30 + getCopperAmount() * 10} ry={5 + getCopperAmount() * 3} fill={copperCycleColors.copperPrecipitate} />
                  <ellipse cx="90" cy="158" rx={25 + getCopperAmount() * 8} ry={4 + getCopperAmount() * 2} fill="#CD7F32" opacity="0.6" />
                  {/* Copper texture */}
                  {getCopperAmount() > 0.5 && (
                    <g>
                      {[...Array(5)].map((_, i) => (
                        <circle key={i} cx={70 + i * 10} cy={158} r={2} fill="#D4A373" opacity="0.5" />
                      ))}
                    </g>
                  )}
                </g>

                {/* Stirring rod when reacting */}
                {reacting && (
                  <g>
                    <line x1="90" y1="20" x2="90" y2="130" stroke="#777" strokeWidth="5" strokeLinecap="round">
                      <animateTransform
                        attributeName="transform"
                        type="rotate"
                        values="0 90 130;12 90 130;-12 90 130;0 90 130"
                        dur="0.5s"
                        repeatCount="indefinite"
                      />
                    </line>
                  </g>
                )}

                {/* Hot plate */}
                <rect x="20" y="175" width="140" height="45" fill="#3A3A3A" rx="5" />
                <g opacity={heatingStarted ? 1 : 0.3}>
                  <circle cx="90" cy="192" r="22" fill="none" stroke={heatingStarted ? "#FF4500" : "#4A4A4A"} strokeWidth="2">
                    {heatingStarted && (
                      <animate attributeName="stroke" values="#CD5C5C;#FF4500;#FF6347" dur="1s" repeatCount="indefinite" />
                    )}
                  </circle>
                  <circle cx="90" cy="192" r="14" fill="none" stroke={heatingStarted ? "#FF4500" : "#4A4A4A"} strokeWidth="2" />
                </g>
                <circle cx="150" cy="205" r="3" fill={heatingStarted ? "#00FF00" : "#333"} />

                <text x="90" y="235" fontSize="10" fill="#E0E0E0" textAnchor="middle">Reaction Vessel</text>
              </svg>
            </div>
          </div>

          {/* Status Messages */}
          <div className="status-area">
            {currentStep === 1 && !zincAdded && (
              <div className="status-message info">
                <Zap size={20} />
                Add zinc powder in small portions while stirring
              </div>
            )}
            {reacting && reactionProgress < 0.7 && (
              <div className="status-message reaction">
                <FlaskConical size={20} />
                Reaction in progress... H₂ bubbles evolving, color fading
              </div>
            )}
            {currentStep === 3 && !reactionComplete && (
              <div className="status-message warning">
                <AlertTriangle size={20} />
                Click to heat gently - complete the reaction
              </div>
            )}
            {reactionComplete && (
              <div className="status-message success">
                <Check size={20} />
                Copper metal recovered! Note the copper-colored precipitate.
              </div>
            )}
          </div>

          {/* Theory Box */}
          <div className="theory-box">
            <h4>Metal Displacement (Redox)</h4>
            <div className="equation">
              CuSO₄ + Zn → ZnSO₄ + Cu
            </div>
            <p>
              Zinc is more reactive than copper (higher in the activity series).
              Zinc is oxidized (Zn → Zn²⁺ + 2e-) while copper is reduced (Cu²⁺ + 2e- → Cu).
              The blue color fades as Cu²⁺ ions are converted to copper metal.
            </p>
            <div className="activity-series">
              <span className="more-reactive">More Reactive: </span>
              <span className="metal zn">Zn</span>
              <span className="arrow">→</span>
              <span className="metal cu">Cu</span>
              <span className="less-reactive"> :Less Reactive</span>
            </div>
          </div>
        </div>

        <style>{`
          .reaction-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .equipment-row {
            display: flex;
            gap: 50px;
            align-items: flex-end;
            justify-content: center;
          }

          .equipment-station {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 10px;
            border-radius: 8px;
            transition: all 0.2s ease;
          }

          .equipment-station.clickable {
            cursor: pointer;
          }

          .equipment-station.clickable:hover {
            background: rgba(0, 168, 107, 0.1);
          }

          .equipment-label {
            font-size: 11px;
            color: var(--text-secondary);
            text-align: center;
          }

          .safety-warning {
            display: flex;
            align-items: center;
            gap: 8px;
            background: rgba(239, 68, 68, 0.2);
            color: #EF4444;
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 11px;
            margin: 8px 0;
          }

          .color-progress {
            margin-top: 12px;
            padding: 10px;
            background: var(--bg-elevated);
            border-radius: 6px;
          }

          .color-progress .label {
            font-size: 11px;
            color: var(--text-secondary);
            display: block;
            margin-bottom: 8px;
          }

          .fade-bar {
            height: 12px;
            background: #222;
            border-radius: 6px;
            overflow: hidden;
            margin-bottom: 6px;
          }

          .fade-fill {
            height: 100%;
            transition: width 0.1s;
            border-radius: 6px;
          }

          .color-labels {
            display: flex;
            justify-content: space-between;
            font-size: 10px;
          }

          .status-area {
            min-height: 50px;
            display: flex;
            justify-content: center;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.info {
            background: rgba(59, 130, 246, 0.2);
            color: #60A5FA;
          }

          .status-message.warning {
            background: rgba(245, 158, 11, 0.2);
            color: #FBBF24;
          }

          .status-message.reaction {
            background: rgba(0, 102, 204, 0.2);
            color: ${copperCycleColors.cuSO4Solution};
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .theory-box {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 500px;
            border-left: 3px solid var(--secondary);
          }

          .theory-box h4 {
            margin: 0 0 8px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .theory-box .equation {
            font-family: 'Courier New', monospace;
            background: rgba(0, 0, 0, 0.3);
            padding: 8px 12px;
            border-radius: 4px;
            margin-bottom: 8px;
            color: ${copperCycleColors.copperPrecipitate};
          }

          .theory-box p {
            margin: 0 0 12px 0;
            font-size: 13px;
            color: var(--text-secondary);
            line-height: 1.5;
          }

          .activity-series {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 12px;
            padding: 8px;
            background: rgba(0, 0, 0, 0.2);
            border-radius: 4px;
          }

          .more-reactive, .less-reactive {
            color: var(--text-secondary);
            font-size: 10px;
          }

          .metal {
            padding: 4px 12px;
            border-radius: 4px;
            font-weight: bold;
          }

          .metal.zn {
            background: ${copperCycleColors.zincPowder};
            color: #333;
          }

          .metal.cu {
            background: ${copperCycleColors.copperPrecipitate};
            color: #FFF;
          }

          .arrow {
            color: var(--secondary);
            font-size: 16px;
          }
        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
        `}</style>
      </LabBench>
      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp5"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
